import KartraClientService from './KartraClient.service';

interface MembershipCheckState {
  lastCheck: number;
  isChecking: boolean;
  checkInterval: number; // in milliseconds
  lastKnownStatus: 'active' | 'inactive' | 'unknown' | null;
  lastKnownActiveAt: number | null;
  consecutiveUnknownChecks: number;
  consecutiveInactiveChecks: number;
}

class MembershipGuardService {
  private state: MembershipCheckState = {
    lastCheck: 0,
    isChecking: false,
    checkInterval: 15 * 60 * 1000, // 15 minutes in production
    lastKnownStatus: null,
    lastKnownActiveAt: null,
    consecutiveUnknownChecks: 0,
    consecutiveInactiveChecks: 0,
  };

  private listeners: Set<() => void> = new Set();

  constructor() {
    // Use shorter interval in development for testing
    if (process.env.NODE_ENV === 'development') {
      this.state.checkInterval = 5 * 60 * 1000; // 5 minutes in development
    }
  }

  /**
   * Initialize the membership guard with navigation-based checking
   */
  initializeWithNavigation() {
    console.log(
      '[MembershipGuard] Initializing with navigation-based checking',
    );

    // Check on app startup
    this.checkMembershipIfNeeded();

    // Listen for route changes (this will be called from App.tsx)
    return this.createNavigationListener();
  }

  /**
   * Create a navigation listener that checks membership on route changes
   */
  createNavigationListener() {
    return () => {
      this.checkMembershipIfNeeded();
    };
  }

  /**
   * Check membership if enough time has passed since last check
   */
  async checkMembershipIfNeeded(): Promise<void> {
    const now = Date.now();
    const timeSinceLastCheck = now - this.state.lastCheck;

    // Skip if we've checked recently or if already checking
    if (
      timeSinceLastCheck < this.state.checkInterval ||
      this.state.isChecking
    ) {
      console.log(
        `[MembershipGuard] Skipping check - last check ${Math.round(timeSinceLastCheck / 1000)}s ago`,
      );
      return;
    }

    await this.performMembershipCheck();
  }

  /**
   * Force a membership check regardless of timing
   */
  async forceCheck(): Promise<boolean> {
    console.log('[MembershipGuard] Forcing membership check');
    return await this.performMembershipCheck();
  }

  /**
   * Perform the actual membership check
   */
  private async performMembershipCheck(): Promise<boolean> {
    const email = localStorage.getItem('email');

    if (!email) {
      console.log(
        '[MembershipGuard] No email found in localStorage, skipping check',
      );
      return true; // Not logged in, so no need to check
    }

    if (!KartraClientService.isConfigured()) {
      console.log(
        '[MembershipGuard] Kartra client not configured, skipping membership check',
      );
      this.state.lastCheck = Date.now();
      return true;
    }

    this.state.isChecking = true;
    this.state.lastCheck = Date.now();

    try {
      console.log(`[MembershipGuard] Checking membership for ${email}`);

      // Retry up to 2 times on 'unknown' with exponential backoff
      let status: 'active' | 'inactive' | 'unknown' = 'unknown';
      const maxAttempts = 3;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        status = await KartraClientService.checkMembershipStatus(email);
        if (status !== 'unknown') break;
        if (attempt < maxAttempts) {
          const delayMs = 1500 * Math.pow(2, attempt - 1);
          console.log(
            `[MembershipGuard] Status unknown, retrying in ${delayMs}ms (attempt ${attempt}/${maxAttempts})`,
          );
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      }

      // Update state based on status
      if (status === 'active') {
        this.state.lastKnownStatus = 'active';
        this.state.lastKnownActiveAt = Date.now();
        this.state.consecutiveUnknownChecks = 0;
        this.state.consecutiveInactiveChecks = 0;
        console.log('[MembershipGuard] Membership confirmed active');
        this.notifyListeners();
        return true;
      }

      if (status === 'inactive') {
        this.state.lastKnownStatus = 'inactive';
        this.state.consecutiveInactiveChecks += 1;
        this.state.consecutiveUnknownChecks = 0;
        console.warn(
          `[MembershipGuard] Membership reported inactive (streak ${this.state.consecutiveInactiveChecks})`,
        );

        // Require 2 consecutive inactive confirmations before logout
        if (this.state.consecutiveInactiveChecks >= 2) {
          // Show alert and logout
          try {
            alert(
              'Your Crate Hackers membership has expired or is no longer active. You will be logged out.',
            );
          } finally {
            import('../utils/logout').then(({ logout }) => {
              logout('membership_expired');
            });
          }
        }

        this.notifyListeners();
        return false;
      }

      // status === 'unknown'
      this.state.lastKnownStatus = 'unknown';
      this.state.consecutiveUnknownChecks += 1;
      console.warn(
        `[MembershipGuard] Membership status unknown (streak ${this.state.consecutiveUnknownChecks}) — will not logout`,
      );
      // Do not logout on unknown; rely on future checks
      this.notifyListeners();
      return false;
    } catch (error) {
      console.error('[MembershipGuard] Error during membership check:', error);
      return false;
    } finally {
      this.state.isChecking = false;
    }
  }

  /**
   * Get the current state of the membership guard
   */
  getState(): MembershipCheckState {
    return { ...this.state };
  }

  /**
   * Subscribe to membership check events
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify all listeners
   */
  private notifyListeners(): void {
    this.listeners.forEach((listener) => {
      try {
        listener();
      } catch (error) {
        console.error('[MembershipGuard] Error calling listener:', error);
      }
    });
  }

  /**
   * Update the check interval (useful for testing)
   */
  setCheckInterval(intervalMs: number): void {
    this.state.checkInterval = intervalMs;
    console.log(`[MembershipGuard] Check interval updated to ${intervalMs}ms`);
  }

  /**
   * Get time until next check
   */
  getTimeUntilNextCheck(): number {
    const now = Date.now();
    const timeSinceLastCheck = now - this.state.lastCheck;
    const timeUntilNext = Math.max(
      0,
      this.state.checkInterval - timeSinceLastCheck,
    );
    return timeUntilNext;
  }

  /**
   * Reset the last check time (useful when user manually refreshes)
   */
  resetLastCheck(): void {
    this.state.lastCheck = 0;
    console.log('[MembershipGuard] Last check time reset');
  }
}

// Export singleton instance
export default new MembershipGuardService();
